/* ============================================================================
   Slide 5 — "Memory & Dreams" — SHARED CONTENT (single source of truth)
   Read by both HTLDSlide5Inner (desktop) and HTLDSlide5Mobile. Edit copy here
   — English and German side by side; the German object must have exactly the
   same shape as the English one.
   ========================================================================== */
import { ByLang, useL } from "i18n/lang";

export const accent5 = "#9b72dd";

export interface Seg { t: string; b?: boolean; em?: boolean; }

export interface JourneyStage { stage: string; color: string; desc: string; }

const journeyColors = [
  "rgba(155,114,221,0.80)",
  "rgba(155,114,221,0.55)",
  "rgba(155,114,221,0.32)",
  "rgba(155,114,221,0.14)",
];

const en = {
  meta: {
    breadcrumbPre: "The Subconscious at Work",
    breadcrumbAccent: "Why forgotten things resurface",
    headerTitlePre: "Memory & ",
    headerTitleAccent: "Dreams",
    titlePre: "Why do ",
    titleAccentWord: "forgotten",
    /** Desktop breaks the line between titlePost1 and titlePost2. */
    titlePost1: " memories",
    titlePost2: "show up in dreams?",
    subtitle: "Nothing is truly forgotten — the subconscious never lets go",
    forgottenLabel: "Nothing is truly forgotten",
    gatekeeperLabel: "Why the conscious mind can't reach them while awake",
    journeyLabel: "The journey of a memory",
    opportunityLabel: "The wellness opportunity",
  },
  journey: [
    { stage: "Experience",  color: journeyColors[0], desc: "Something happens — a loss, a conflict, a feeling." },
    { stage: "Suppression", color: journeyColors[1], desc: "What cannot be processed is pushed below awareness." },
    { stage: "Storage",     color: journeyColors[2], desc: "It waits — stored below, shaping behaviour." },
    { stage: "Resurfacing", color: journeyColors[3], desc: "In sleep the gatekeeper quiets. The memory rises." },
  ] as JourneyStage[],
  forgotten: [
    { t: "The conscious mind forgets — but the subconscious does not. Memories that have been suppressed remain stored below the surface. They do not disappear. " },
    { t: "They wait.", b: true },
    { t: " When the conscious mind quiets in sleep, they rise." },
  ] as Seg[],
  gatekeeper: [
    { t: "The prefrontal cortex acts as a gatekeeper — filtering and suppressing. " },
    { t: "Sleep removes the gatekeeper. The subconscious speaks freely.", b: true },
  ] as Seg[],
  opportunity: [
    { t: "When you become lucid within a dream you can " },
    { t: "face what surfaces, engage with it consciously, and explore it with greater awareness.", b: true },
    { t: " This is the moment Dreamfulness is designed for." },
  ] as Seg[],
  canvas: {
    conscious: "CONSCIOUS MIND",
    subconscious: "SUBCONSCIOUS",
    depths: ["SURFACE", "SHALLOW", "DEEP", "DEEPEST"],
    resurfacing: "↑  RESURFACING IN DREAMS  ↑",
    caption: "memories wait in the deep",
  },
};

export type Slide5Content = typeof en;

const de: Slide5Content = {
  meta: {
    breadcrumbPre: "Das Unterbewusstsein am Werk",
    breadcrumbAccent: "Warum Vergessenes wieder auftaucht",
    headerTitlePre: "Erinnerung & ",
    headerTitleAccent: "Träume",
    titlePre: "Warum tauchen ",
    titleAccentWord: "vergessene",
    titlePost1: " Erinnerungen",
    titlePost2: "in Träumen auf?",
    subtitle: "Nichts ist wirklich vergessen – das Unterbewusstsein lässt nie los",
    forgottenLabel: "Nichts ist wirklich vergessen",
    gatekeeperLabel: "Warum der Wachverstand sie nicht erreicht",
    journeyLabel: "Die Reise einer Erinnerung",
    opportunityLabel: "Die Chance für dein Wohlbefinden",
  },
  journey: [
    { stage: "Erleben",       color: journeyColors[0], desc: "Etwas geschieht – ein Verlust, ein Konflikt, ein Gefühl." },
    { stage: "Verdrängung",   color: journeyColors[1], desc: "Was nicht verarbeitet wird, sinkt ins Unbewusste." },
    { stage: "Speicherung",   color: journeyColors[2], desc: "Es wartet – tief gespeichert, prägt es dein Verhalten." },
    { stage: "Auftauchen",    color: journeyColors[3], desc: "Im Schlaf wird der Wächter still. Die Erinnerung steigt auf." },
  ],
  forgotten: [
    { t: "Der bewusste Verstand vergisst – das Unterbewusstsein nicht. Verdrängte Erinnerungen bleiben unter der Oberfläche gespeichert. Sie verschwinden nicht. " },
    { t: "Sie warten.", b: true },
    { t: " Wenn der Verstand im Schlaf zur Ruhe kommt, steigen sie auf." },
  ],
  gatekeeper: [
    { t: "Der präfrontale Kortex wirkt wie ein Wächter – er filtert und unterdrückt. " },
    { t: "Im Schlaf tritt der Wächter zurück. Das Unterbewusstsein spricht frei.", b: true },
  ],
  opportunity: [
    { t: "Wenn du im Traum luzid wirst, kannst du " },
    { t: "dem Auftauchenden begegnen, dich bewusst darauf einlassen und es achtsam erforschen.", b: true },
    { t: " Genau für diesen Moment ist Dreamfulness gemacht." },
  ],
  canvas: {
    conscious: "BEWUSSTSEIN",
    subconscious: "UNTERBEWUSSTSEIN",
    depths: ["OBERFLÄCHE", "FLACH", "TIEF", "GANZ TIEF"],
    resurfacing: "↑  TAUCHT IM TRAUM AUF  ↑",
    caption: "Erinnerungen warten in der Tiefe",
  },
};

export const slide5Content: ByLang<Slide5Content> = { en, de };
export const useSlide5 = () => useL(slide5Content);
